import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DatePicker, Tag } from 'antd';
import { Search, DataGrid } from '@hydrodigital/design-system';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';
import { apiController } from '@/utils/api';
import { getSimulationHistory } from '../../services/simulations';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import useDebounce from '@/hooks/useDebounce';

interface DataType {
  key: string;
  id: string;
  inp: number;
  status: string;
  statusMessage: string;
  startedAt: string;
  finishedAt: string;
  timeExecution: string;
  locationName: string;
}

const rangePresets: {
  label: string;
  value: [Dayjs, Dayjs];
}[] = [
  { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
  { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
  { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
  { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
];

const statusOptions: ComboboxOption[] = [
  { value: 'COMPLETED', label: 'COMPLETED' },
  { value: 'IN PROGRESS', label: 'IN PROGRESS' },
  { value: 'FAILED', label: 'FAILED' },
];

const Simulations: React.FC = () => {
  const [simulationId, setSimulationId] = useState<string>('');
  const [committedId, setCommittedId] = useState<string>('');
  const [startdate, setStartDate] = useState<any>(null);
  const [enddate, setEndDate] = useState<any>(null);
  const [dates, setDates] = useState<any>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [simulations, setSimulations] = useState<any>([]);
  const [paginationState, setPaginationState] = useState({ pageIndex: 0, pageSize: 10 });
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const debouncedSimulationId = useDebounce(committedId, 50);


  const prevFiltersRef = useRef({ debouncedSimulationId, startdate, enddate, status });
  const fetchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleUpdateHistory = useCallback(async () => {
    try {
      setLoading(true);
      let organizationId = [];
      const printItems = localStorage.getItem('security');
      if (printItems) {
        organizationId = JSON.parse(printItems).organizationId;
      }

      const requestedPage = debouncedSimulationId ? 1 : paginationState.pageIndex + 1;
      const requestedSize = debouncedSimulationId ? 1000 : paginationState.pageSize;

      const response = await getSimulationHistory(organizationId, {
        page: requestedPage,
        size: requestedSize,
        simulationId: debouncedSimulationId || null,
        startdate: startdate && `${startdate} 00:00:00`,
        enddate: startdate && `${enddate} 00:00:00`,
        status,
      });

      if (response) {
        const list = Array.isArray(response.simulationsResponseDTOList)
          ? response.simulationsResponseDTOList
          : [];
        const filtered = debouncedSimulationId
          ? list.filter((s: any) => String(s?.id ?? '').startsWith(debouncedSimulationId))
          : list;
        setTotalRows(
          debouncedSimulationId ? filtered.length : response.totalSimulations || filtered.length || 0,
        );
        setSimulations(filtered);
      }
    } catch (error) {
      console.error('Error fetching simulations:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSimulationId, paginationState.pageIndex, paginationState.pageSize, startdate, enddate, status]);


  const hasInProgressSimulations = useCallback(() => {
    return simulations.some((sim: any) => sim.status === 'IN_PROGRESS');
  }, [simulations]);


  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged =
      prevFilters.debouncedSimulationId !== debouncedSimulationId ||
      prevFilters.startdate !== startdate ||
      prevFilters.enddate !== enddate ||
      prevFilters.status !== status;

    if (filtersChanged) {
      prevFiltersRef.current = { debouncedSimulationId, startdate, enddate, status };
      
      if (paginationState.pageIndex !== 0) {
        setPaginationState((p) => ({ ...p, pageIndex: 0 }));
        return; 

      }
    }

    handleUpdateHistory();

    return () => {
      apiController.abort();
    };
  }, [handleUpdateHistory, debouncedSimulationId, startdate, enddate, status, paginationState.pageIndex]);

 
  useEffect(() => {
    
    if (fetchIntervalRef.current) {
      clearInterval(fetchIntervalRef.current);
      fetchIntervalRef.current = null;
    }

    
    if (hasInProgressSimulations()) {
      fetchIntervalRef.current = setInterval(() => {
        handleUpdateHistory();
      }, 10000);
    }

    return () => {
      if (fetchIntervalRef.current) {
        clearInterval(fetchIntervalRef.current);
        fetchIntervalRef.current = null;
      }
    };
  }, [hasInProgressSimulations, handleUpdateHistory]);

  const disabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'years') >= 2;
    const tooEarly = dates[1] && dates[1].diff(current, 'years') >= 2;
    return !!tooEarly || !!tooLate;
  };

  const { RangePicker } = DatePicker;

  const columns = [
    {
      accessorKey: 'id',
      header: 'Simulation ID',
      cell: (cellContext: any) => (
        <a style={{ color: '#078CB3' }}>{cellContext.getValue()}</a>
      ),
    },
    {
      accessorKey: 'inp',
      header: 'INP File',
      cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
    },
    {
      accessorKey: 'locationName',
      header: 'Location',
      cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (cellContext: any) => {
        const text = cellContext.getValue();
        const fontColor = '#232B36';
        let color = '#FFEECB';
        if (text === 'COMPLETED') {
          color = '#F7F9FC';
        }
        if (text === 'SUCCESS') {
          color = '#C0EFDE';
        }
        if (text === 'IN PROGRESS') {
          color = '#FFEECB';
        }
        if (text === 'FAILED') {
          color = '#FFD3D3';
        }
        return (
          <Tag color={color} style={{ color: fontColor }} key={text}>
            {text}
          </Tag>
        );
      },
    },
    {
      accessorKey: 'statusMessage',
      header: 'Status Message',
      cell: (cellContext: any) => (
        <span className="table-data-gis">{cellContext.getValue()}</span>
      ),
    },
    {
      accessorKey: 'startedAt',
      header: 'Started at',
      cell: (cellContext: any) => {
        const row = cellContext.row.original as DataType;
        return <span>{dayjs(row.startedAt).format('YYYY/MM/DD [at] hh:mm a')}</span>;
      },
    },
    {
      accessorKey: 'finishedAt',
      header: 'Finished at',
      cell: (cellContext: any) => {
        const row = cellContext.row.original as DataType;
        return <span>{dayjs(row.finishedAt).format('YYYY/MM/DD [at] hh:mm a')}</span>;
      },
    },
    {
      accessorKey: 'timeExecution',
      header: 'Time execution (minutes)',
      cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
    },
  ];

  return (
    <div>
      <div className="mt-5 flex">
        <div style={{ width: 200, minWidth: 200 }}>
          <Search
            placeholder="Search ID"
            size="small"
            value={simulationId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const onlyDigits = e.target.value.replace(/\D+/g, '');
              setSimulationId(onlyDigits);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                setCommittedId(simulationId);
              }
            }}
            onBlur={() => {
              setCommittedId(simulationId);
            }}
            showClearButton={true}
            onClear={() => {
              setSimulationId('');
              setCommittedId('');
            }}
            style={{ width: '100%' }}
          />
        </div>
        <RangePicker
          onChange={(dates: null | (Dayjs | null)[], dateStrings: string[]) => {
            if (dates) {
              setStartDate(dateStrings[0]);
              setEndDate(dateStrings[1]);
            } else {
              setStartDate(null);
              setEndDate(null);
            }
          }}
          disabledDate={disabledDate}
          presets={rangePresets}
          onCalendarChange={(val) => {
            setDates(val);
          }}
          format="YYYY-MM-DD"
          style={{ marginLeft: 30, width: 300 }}
        />
        <div style={{ marginLeft: 30, width: 160 }}>
          <Combobox
            items={statusOptions}
            onValueChange={(val) => setStatus(val)}
            onClear={() => setStatus(null)}
            searchable={false}
            size="small"
          >
            <ComboboxInput placeholder="Status" />
            <ComboboxContent>
              <ComboboxEmpty>No options.</ComboboxEmpty>
              <ComboboxList>
                {(item) => (
                  <ComboboxItem key={item.value} value={item}>
                    {item.label}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </div>
      </div>
      <div className="mt-3">
        <DataGrid
          columns={columns}
          data={simulations || []}
          loading={loading && simulations.length === 0}
          striped={true}
          hoverable={true}
          bordered={true}
          stickyHeader={true}
          maxHeight="calc(100vh - 420px)"
          pagination={{
            enabled: true,
            manualPagination: true,
            rowCount: totalRows,
            defaultPageSize: 10,
            pageSizeOptions: [10, 20, 50],
            showPageSizeSelector: true,
            showPageInfo: true,
          }}
          paginationState={paginationState}
          onPaginationChange={setPaginationState}
        />
      </div>
    </div>
  );
};

export default Simulations;
